import React, { useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import AsyncCreatableSelect from "react-select/async-creatable";
import FanFilterSelect from "./FanFilterSelect";
import FilterErrorIcon from "./FilterErrorIcon";

import client from "../../../../shared/api/client";
import { toastError } from "../../../../shared/toastHelper";
import { formInputContainerStyle } from "./FanFilterGroupForm";

const SEARCH_DEBOUNCE_MS = 250;
const TAGS_PER_PAGE = 50;

const TagDataForm = (props) => {
  const {
    team,
    fanFilter,
    updateFanFilter
  } = props;

  const supportedTagDataTypes = fanFilter.filter_param_options.tag_data_type || [];
  const supportedConditions = fanFilter.filter_param_options.condition || [];

  const tagDataType = fanFilter.filter_params.tag_data_type || "";
  const condition = fanFilter.filter_params.condition || "";
  const tagName = (fanFilter.filter_params.values || [])[0] || "";

  const fetchTags = useCallback((inputValue, callback) => {
    const params = { scope: "Fan", paginated: true, per_page: TAGS_PER_PAGE };
    if (inputValue) params.name = inputValue;

    client.get(`/teams/${team.id}/crm/tags`, { params })
      .then((response) => {
        const tags = (response.data && response.data.tags) || [];
        callback(tags.map((tag) => ({ value: tag.name, label: tag.name })));
      })
      .catch(() => {
        callback([]);
        toastError("Could not load tags, please try again");
      });
  }, [team.id]);

  const debouncedFetchTags = useMemo(() => _.debounce(fetchTags, SEARCH_DEBOUNCE_MS), [fetchTags]);

  // An empty search is the menu opening, not typing: debouncing it lets a quick
  // keystroke swallow the callback react-select needs for defaultOptions.
  const loadTagOptions = (inputValue, callback) => {
    if (inputValue) {
      debouncedFetchTags(inputValue, callback);
    } else {
      fetchTags(inputValue, callback);
    }
  };

  useEffect(() => () => debouncedFetchTags.cancel(), [debouncedFetchTags]);

  const setTagName = (name) => {
    updateFanFilter({
      filter_params: {
        ...fanFilter.filter_params,
        values: [name],
      },
    });
  };

  // Clicking away is the one gesture that does not commit a typed value — Enter,
  // Tab and clicking the option all do — and an uncommitted filter is dropped
  // from the audience by `fan_filters.validated` rather than surfacing an error.
  const commitTypedValueOnBlur = (inputValue, { action, prevInputValue }) => {
    if (action !== "input-blur") return;

    const typed = (prevInputValue || "").trim();
    if (typed && typed !== tagName) setTagName(typed);
  };

  return (
    <>
      <div style={formInputContainerStyle}>
        <FanFilterSelect
          aria-label="Select Type"
          placeholder="Select Type"
          options={supportedTagDataTypes.map((option) => ({ value: option, label: option }))}
          value={tagDataType || null}
          onChange={(value) => updateFanFilter({
            filter_params: {
              ...fanFilter.filter_params,
              tag_data_type: value,
              condition: "",
              values: [""],
            },
          })}
        />
      </div>
      {tagDataType && (
        <div style={formInputContainerStyle}>
          <FanFilterSelect
            aria-label="Select Condition"
            placeholder="Select Condition"
            options={supportedConditions.map((option) => ({ value: option, label: option }))}
            value={condition || null}
            onChange={(value) => {
              updateFanFilter({
                filter_params: {
                  ...fanFilter.filter_params,
                  condition: value,
                  values: [""],
                },
              });
            }}
          />
        </div>
      )}
      {condition && (
        <div style={formInputContainerStyle}>
          <AsyncCreatableSelect
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            placeholder="Search tags"
            defaultOptions
            value={tagName ? { value: tagName, label: tagName } : null}
            loadOptions={loadTagOptions}
            isClearable
            allowCreateWhileLoading
            createOptionPosition="first"
            onChange={(option) => setTagName(option ? option.value : "")}
            onCreateOption={(inputValue) => setTagName(inputValue.trim())}
            onInputChange={commitTypedValueOnBlur}
            isValidNewOption={(inputValue, selectValue, selectOptions) => {
              const typed = inputValue.trim().toLowerCase();
              return !!typed && !selectOptions.some((option) => option.label.toLowerCase() === typed);
            }}
            formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
            noOptionsMessage={() => "No tags match"}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                minWidth: "260px",
                borderRadius: "8px",
                border: tagName ? "1px solid var(--sl-border-primary)" : "1px solid var(--sl-border-error)",
              }),
              menuPortal: (baseStyles) => ({ ...baseStyles, zIndex: 9999 }),
              menu: (baseStyles) => ({
                ...baseStyles,
                width: "max-content",
                maxWidth: "500px",
              }),
            }}
          />
        </div>
      )}

      <FilterErrorIcon errors={fanFilter.filter_validation_errors} />
    </>
  );
};

TagDataForm.propTypes = {
  team: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  }).isRequired,
  fanFilter: PropTypes.shape({
    filter_params: PropTypes.object.isRequired,
    filter_param_options: PropTypes.object.isRequired,
    filter_validation_errors: PropTypes.array.isRequired,
  }).isRequired,
  updateFanFilter: PropTypes.func.isRequired,
};

export default TagDataForm;
